import { relations, sql } from "drizzle-orm";
import {
  check,
  doublePrecision,
  foreignKey,
  jsonb,
  pgEnum,
  pgTable,
  text,
  unique,
  uuid,
} from "drizzle-orm/pg-core";
import { user } from "../../auth/core/user.sql";
import { scope } from "../../auth/scope/scope.sql";
import { id, sortIndex, temporalInstant } from "../../utils/sql";
import { FoodItemMatcher } from "../food-matcher";
import { foodServing } from "../food/food.sql";
import { foodLogSourceGroup } from "../food-log/food-log.sql";
import type { Nutrient } from "../nutrient";

export const plannedMealV2Status = pgEnum("planned_meal_v2_status", ["draft", "published"]);

export const plannedMealV2 = pgTable(
  "planned_meal_v2",
  {
    ...id,
    timeCreated: temporalInstant("time_created").notNull(),
    timeUpdated: temporalInstant("time_updated").notNull(),
    scopeId: uuid("scope_id")
      .references(() => scope.id)
      .notNull(),
    typeLabel: text("type_label").notNull(),
    status: plannedMealV2Status("status").notNull(),
    name: text("name").notNull(),
  },
  (t) => [
    unique("planned_meal_v2_scope_id_unique").on(t.scopeId, t.id),
    check("planned_meal_v2_type_label_non_empty", sql`length(trim(${t.typeLabel})) > 0`),
  ],
);

export const plannedMealV2Relations = relations(plannedMealV2, ({ one, many }) => ({
  scope: one(scope, {
    fields: [plannedMealV2.scopeId],
    references: [scope.id],
  }),
  plates: many(plannedMealPlateV2),
  events: many(plannedMealEventV2),
}));

export const plannedMealEventV2 = pgTable(
  "planned_meal_event_v2",
  {
    ...id,
    timeCreated: temporalInstant("time_created").notNull(),
    timeUpdated: temporalInstant("time_updated").notNull(),
    mealId: uuid("meal_id")
      .references(() => plannedMealV2.id)
      .notNull(),
    timeAt: temporalInstant("time_at").notNull(),
    timeZone: text("time_zone").notNull(),
  },
  (t) => [unique("planned_meal_event_v2_meal_event_unique").on(t.mealId, t.id)],
);

export const plannedMealEventV2Relations = relations(plannedMealEventV2, ({ one, many }) => ({
  plannedMeal: one(plannedMealV2, {
    fields: [plannedMealEventV2.mealId],
    references: [plannedMealV2.id],
  }),
  assignments: many(plannedMealEventAssignmentV2),
}));

export const plannedMealPlateV2 = pgTable(
  "planned_meal_plate_v2",
  {
    ...id,
    mealId: uuid("meal_id")
      .references(() => plannedMealV2.id)
      .notNull(),
    userId: uuid("user_id")
      .references(() => user.id)
      .notNull(),
    nutrientTargets: jsonb("nutrient_targets").$type<Nutrient.Targets | null>(),
  },
  (t) => [
    unique("planned_meal_plate_v2_meal_plate_unique").on(t.mealId, t.id),
    unique("planned_meal_plate_v2_meal_plate_user_unique").on(t.mealId, t.id, t.userId),
  ],
);

export const plannedMealPlateV2Relations = relations(plannedMealPlateV2, ({ one, many }) => ({
  plannedMeal: one(plannedMealV2, {
    fields: [plannedMealPlateV2.mealId],
    references: [plannedMealV2.id],
  }),
  items: many(plannedMealPlateItemV2),
  itemDescriptors: many(plannedMealPlateItemDescriptorV2),
  assignments: many(plannedMealEventAssignmentV2),
}));

export const plannedMealEventAssignmentV2 = pgTable(
  "planned_meal_event_assignment_v2",
  {
    ...id,
    timeCreated: temporalInstant("time_created").notNull(),
    timeUpdated: temporalInstant("time_updated").notNull(),
    mealId: uuid("meal_id").notNull(),
    eventId: uuid("event_id").notNull(),
    userId: uuid("user_id")
      .references(() => user.id)
      .notNull(),
    plateId: uuid("plate_id").notNull(),
  },
  (t) => [
    unique("planned_meal_event_assignment_v2_meal_assignment_unique").on(t.mealId, t.id),
    unique("planned_meal_event_assignment_v2_meal_event_user_unique").on(
      t.mealId,
      t.eventId,
      t.userId,
    ),
    unique("planned_meal_event_assignment_v2_meal_event_plate_unique").on(
      t.mealId,
      t.eventId,
      t.plateId,
    ),
    foreignKey({
      name: "planned_meal_event_assignment_v2_event_fk",
      columns: [t.mealId, t.eventId],
      foreignColumns: [plannedMealEventV2.mealId, plannedMealEventV2.id],
    }),
    foreignKey({
      name: "planned_meal_event_assignment_v2_plate_user_fk",
      columns: [t.mealId, t.plateId, t.userId],
      foreignColumns: [plannedMealPlateV2.mealId, plannedMealPlateV2.id, plannedMealPlateV2.userId],
    }),
  ],
);

export const plannedMealEventAssignmentV2Relations = relations(
  plannedMealEventAssignmentV2,
  ({ one }) => ({
    event: one(plannedMealEventV2, {
      fields: [plannedMealEventAssignmentV2.mealId, plannedMealEventAssignmentV2.eventId],
      references: [plannedMealEventV2.mealId, plannedMealEventV2.id],
    }),
    plate: one(plannedMealPlateV2, {
      fields: [
        plannedMealEventAssignmentV2.mealId,
        plannedMealEventAssignmentV2.plateId,
        plannedMealEventAssignmentV2.userId,
      ],
      references: [plannedMealPlateV2.mealId, plannedMealPlateV2.id, plannedMealPlateV2.userId],
    }),
    foodLogSourceGroup: one(foodLogSourceGroup, {
      fields: [plannedMealEventAssignmentV2.id],
      references: [foodLogSourceGroup.sourceId],
    }),
  }),
);

export const plannedMealPlateItemV2 = pgTable(
  "planned_meal_plate_item_v2",
  {
    ...id,
    timeCreated: temporalInstant("time_created").notNull(),
    timeUpdated: temporalInstant("time_updated").notNull(),
    ...sortIndex,
    mealId: uuid("meal_id").notNull(),
    plateId: uuid("plate_id").notNull(),
    foodServingId: uuid("food_serving_id")
      .notNull()
      .references(() => foodServing.id),
    amount: doublePrecision("amount").notNull(),
  },
  (t) => [
    unique("planned_meal_plate_item_v2_meal_plate_sort_index_unique").on(
      t.mealId,
      t.plateId,
      t.sortIndex,
    ),
    unique("planned_meal_plate_item_v2_meal_plate_food_serving_unique").on(
      t.mealId,
      t.plateId,
      t.foodServingId,
    ),
    foreignKey({
      name: "planned_meal_plate_item_v2_plate_fk",
      columns: [t.mealId, t.plateId],
      foreignColumns: [plannedMealPlateV2.mealId, plannedMealPlateV2.id],
    }),
  ],
);

export const plannedMealPlateItemV2Relations = relations(plannedMealPlateItemV2, ({ one }) => ({
  plate: one(plannedMealPlateV2, {
    fields: [plannedMealPlateItemV2.mealId, plannedMealPlateItemV2.plateId],
    references: [plannedMealPlateV2.mealId, plannedMealPlateV2.id],
  }),
  serving: one(foodServing, {
    fields: [plannedMealPlateItemV2.foodServingId],
    references: [foodServing.id],
  }),
}));

export const plannedMealPlateItemDescriptorV2 = pgTable(
  "planned_meal_plate_item_descriptor_v2",
  {
    ...id,
    timeCreated: temporalInstant("time_created").notNull(),
    timeUpdated: temporalInstant("time_updated").notNull(),
    ...sortIndex,
    mealId: uuid("meal_id").notNull(),
    plateId: uuid("plate_id").notNull(),
    data: jsonb("data").$type<FoodItemMatcher.ItemDescriptor>().notNull(),
  },
  (t) => [
    unique("planned_meal_plate_item_descriptor_v2_sort_unique").on(
      t.mealId,
      t.plateId,
      t.sortIndex,
    ),
    foreignKey({
      name: "planned_meal_plate_item_descriptor_v2_plate_fk",
      columns: [t.mealId, t.plateId],
      foreignColumns: [plannedMealPlateV2.mealId, plannedMealPlateV2.id],
    }),
  ],
);

export const plannedMealPlateItemDescriptorV2Relations = relations(
  plannedMealPlateItemDescriptorV2,
  ({ one }) => ({
    plate: one(plannedMealPlateV2, {
      fields: [plannedMealPlateItemDescriptorV2.mealId, plannedMealPlateItemDescriptorV2.plateId],
      references: [plannedMealPlateV2.mealId, plannedMealPlateV2.id],
    }),
  }),
);
